/**
 * The canvas floating chat window body: the shared FloatingChat header
 * (session switcher, new chat, size toggle, minimize) plus the shared chat
 * stack (see `components/FloatingChat/` — the workspace twin,
 * `WorkspaceFloatingChatPane`, composes the same pieces). Canvas-specific
 * flavor: sessions are minted here (canvas-class, with a create spinner in the
 * header), the composer honors onboarding's pinned/locked prompt, and canvas
 * selection entities ride on sends.
 */

import { useCanvasFlowSessions } from "@/api/canvas";
import {
  useCreateFlowSession,
  useDeleteFlowSession,
  useFlowSessionById,
} from "@/api/flow_sessions";
import { useFlowMessages } from "@/api/messages";
import { FloatingChatBody } from "@/components/FloatingChat/FloatingChatBody";
import {
  FloatingChatEmptyState,
  type FloatingChatSuggestedAction,
} from "@/components/FloatingChat/FloatingChatEmptyState";
import { FloatingChatHeader } from "@/components/FloatingChat/FloatingChatHeader";
import { FloatingChatPaneShell } from "@/components/FloatingChat/FloatingChatPaneShell";
import { MessageInput } from "@/components/MessageInput";
import { ReferencedEntitiesPills } from "@/components/ReferencedEntitiesPills";
import { useComposerDraft } from "@/composer/composerDraft";
import { MessageInputControlSet } from "@/constants/message-input";
import { useOnDrop } from "@/dnd/OsFileDropLayer";
import { useMessageActions } from "@/hooks/useMessageActions";
import Sentry from "@/integrations/Sentry";
import {
  FLOW_SESSION_MODES,
  PostMessageParams,
  ReferencedEntity,
} from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { FileText, HelpCircle, Lightbulb, Mail } from "lucide-react";
import { useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { CANVAS_SESSION_MESSAGE_INPUT_CLASSNAMES } from "../config/canvasSessionMessageInputClassNames";
import { type CanvasFloatingSessionHistory } from "../context/CanvasFloatingChatContext";
import { useCanvasOnboardingState } from "../context/CanvasOnboardingContext";
import { useCanvasReasoningEffort } from "../hooks/useCanvasReasoningEffort";

export interface CanvasSessionPaneContentProps {
  canvasId: string;
  flowSessionId: string;
  canGoBack: boolean;
  onGoBack: () => void;
  onClose: () => void;
  /** True when in the full-height (docked) size class — drives the expand-toggle icon. */
  isChatExpanded: boolean;
  /** Flip between windowed and full-height size classes. */
  onToggleExpand: () => void;
  onOpenSession: (
    sessionId: string,
    history?: CanvasFloatingSessionHistory,
  ) => void;
  /** Notifies the shell when the session-switcher dropdown opens. */
  onMenuOpenChange?: (open: boolean) => void;
  /** Canvas selection entities (owned by the panel; shared with the floating pills). */
  referencedEntities: ReferencedEntity[];
  onRemoveEntity: (entityId: string) => void;
  clearTextSelectionsFromMessage: (
    entities: ReferencedEntity[] | undefined,
  ) => void;
}

// Suggested actions for the canvas agent empty state
const SUGGESTED_ACTIONS: FloatingChatSuggestedAction[] = [
  {
    id: "add-emails",
    label: "Add my recent emails",
    message: "Add my last 10 emails to the canvas",
    icon: Mail,
  },
  {
    id: "brainstorm",
    label: "Brainstorm ideas",
    message:
      "Give me 5 creative product ideas as text notes. Use markdown formatting and different colors creatively to make them visually engaging!",
    icon: Lightbulb,
  },
  {
    id: "create-outline",
    label: "Create a project outline",
    message: "Create a project outline document for a new initiative",
    icon: FileText,
  },
  {
    id: "help",
    label: "What can you do?",
    message: "What are all the things you can help me with on this canvas?",
    icon: HelpCircle,
  },
];

// Inner component that has access to SessionProvider
function CanvasSessionPaneInner({
  flowSessionId,
  canvasId,
  referencedEntities,
  onRemoveEntity,
  clearTextSelectionsFromMessage,
}: {
  flowSessionId: string;
  canvasId: string;
  /** Selected canvas entities (owned by the panel so they can also float above the input). */
  referencedEntities: ReferencedEntity[];
  onRemoveEntity: (entityId: string) => void;
  clearTextSelectionsFromMessage: (
    entities: ReferencedEntity[] | undefined,
  ) => void;
}) {
  // A prompt seeded by another surface (e.g. "Create with Agent") for this
  // session, claimed once. A later remount — after the user sent it, or on a
  // switch to a different session — finds nothing and starts blank.
  const composerDraft = useComposerDraft(flowSessionId);

  // Check if session has any messages
  const {
    data: messages = [],
    refetch: refetchMessages,
    isPending: isMessagesPending,
  } = useFlowMessages(flowSessionId, undefined, {
    enabled: !!flowSessionId,
  });

  // Track current session status so we can disable the input while processing
  const { data: flowSession } = useFlowSessionById(flowSessionId, {
    enabled: !!flowSessionId,
  });
  const isSendDisabled = flowSession
    ? flowSession.status !== "ready" && flowSession.status !== "initialized"
    : false;

  // Onboarding chats run in a dedicated mode. Their composer is locked to the
  // scripted prompt — the user can send it but not edit or type their own
  // message to the onboarding agent.
  const isOnboardingSession =
    flowSession?.mode === FLOW_SESSION_MODES.CANVAS_ONBOARDING;

  const [reasoningEffort, setReasoningEffort] =
    useCanvasReasoningEffort(canvasId);

  // Canvas-scoped chat uploads land as `class=canvas` file_records (per the
  // Files attached in canvas-scoped chat are flow-session-scoped
  // (matches standalone chat). They ride on the message via
  // `file_record_ids` and the agent reads them through `file-list` /
  // `file-get`, but they don't auto-spawn a Loro node on the canvas.
  // Users who want a file ON the canvas drop it directly via the
  // canvas drop target / paste / quick-add surfaces.
  const {
    onSendMessage: onSendMessageBase,
    isLoading,
    uploadedFiles,
    onAttachFile,
    onAttachFileRecords,
    onRemoveFile,
  } = useMessageActions({
    onMessageSent: () => {
      setTimeout(() => refetchMessages(), 500);
    },
  });

  // Own the chat drop target so dropped files land on this hook's upload
  // queue (the visible composer), not the hidden ChatArea instance.
  useOnDrop(onAttachFile);

  // Onboarding can pin a pre-filled composer prompt. When set, the
  // MessageInput's Lexical editor is hydrated with the prompt text as
  // its default value — the user can edit it before sending. On send,
  // we dispatch a window event so the active step can react (e.g.
  // start the frontend artifact-seed pipeline) without needing a
  // direct callback ref into the step.
  const { pinnedComposerPrompt, activeOnboarding } = useCanvasOnboardingState();
  // Onboarding suppresses the empty-state suggested actions in the canvas
  // chat. They distract from the onboarding overlay's own focused prompt and
  // pull the user toward off-flow tangents. Auto-derived from the presence of
  // an active onboarding session — no explicit prop wiring needed at call
  // sites.
  const suppressComposerSuggestions = !!activeOnboarding;

  const onSendMessage = useCallback(
    async (params: PostMessageParams) => {
      await onSendMessageBase(params);
      clearTextSelectionsFromMessage(params.referenced_entities);
      // Only a send from the dedicated onboarding session may advance a
      // scripted step. Without this guard, a user who switched the
      // floating chat to the regular canvas agent could fire the
      // step-advance event from the wrong conversation.
      if (pinnedComposerPrompt && isOnboardingSession) {
        window.dispatchEvent(
          new CustomEvent("cohesive:onboarding:composer-prompt-sent", {
            detail: { id: pinnedComposerPrompt.id },
          }),
        );
      }
    },
    [
      onSendMessageBase,
      pinnedComposerPrompt,
      isOnboardingSession,
      clearTextSelectionsFromMessage,
    ],
  );

  const handleSuggestedAction = useCallback(
    (action: FloatingChatSuggestedAction) => {
      onSendMessage({
        id: uuidv4(),
        content: action.message,
        referenced_entities: referencedEntities,
      } as PostMessageParams);
    },
    [onSendMessage, referencedEntities],
  );

  const hasMessages = messages.length > 0;

  return (
    <FloatingChatBody
      isMessagesPending={isMessagesPending}
      hasMessages={hasMessages}
      controlSet={MessageInputControlSet.CANVAS_CHAT}
      placeholder="What would you like me to do?"
      referencedEntities={referencedEntities}
      onSendSuccess={clearTextSelectionsFromMessage}
      emptyState={
        <FloatingChatEmptyState
          hint="Mention a node or file with @, or run a Skill with the spark."
          actions={SUGGESTED_ACTIONS}
          onAction={handleSuggestedAction}
          // Hidden during onboarding so the pinned overlay prompt is the only
          // call-to-action.
          showSuggestions={!suppressComposerSuggestions}
        />
      }
    >
      <MessageInput
        flowSessionId={flowSessionId}
        canvasId={canvasId}
        onSend={onSendMessage}
        // Hydrate the scripted prompt only into the onboarding session's
        // composer — never a regular canvas-agent chat the user opened. Any
        // other chat takes this session's seeded draft, when one is pending.
        defaultValue={
          isOnboardingSession ? pinnedComposerPrompt?.message : composerDraft
        }
        referencedEntities={referencedEntities}
        attachedFiles={uploadedFiles}
        onAttachFile={onAttachFile}
        onAttachFileRecords={onAttachFileRecords}
        onRemoveFile={onRemoveFile}
        sendButtonDisabled={isSendDisabled || isLoading}
        editingLocked={isOnboardingSession}
        controlSet={MessageInputControlSet.CANVAS_CHAT}
        location="sidebar"
        placeholder="What would you like me to do?"
        autoFocus={!isMessagesPending && !hasMessages}
        className="shrink-0 px-3 pb-3"
        classNames={CANVAS_SESSION_MESSAGE_INPUT_CLASSNAMES}
        agentReasoningEffort={reasoningEffort}
        onAgentReasoningEffortChange={setReasoningEffort}
        middleToolbarSlot={
          referencedEntities.length > 0 ? (
            <ReferencedEntitiesPills
              entities={referencedEntities}
              onRemove={onRemoveEntity}
              className="px-0 py-0"
            />
          ) : null
        }
      />
    </FloatingChatBody>
  );
}

/** Floating canvas agent chat — header, session chrome, and chat body */
export function CanvasSessionPaneContent({
  canvasId,
  flowSessionId,
  canGoBack,
  onGoBack,
  onClose,
  isChatExpanded,
  onToggleExpand,
  onOpenSession,
  onMenuOpenChange,
  referencedEntities,
  onRemoveEntity,
  clearTextSelectionsFromMessage,
}: CanvasSessionPaneContentProps) {
  const queryClient = useQueryClient();
  const createFlowSession = useCreateFlowSession();
  const deleteFlowSession = useDeleteFlowSession();
  const [showSessionSelector, setShowSessionSelector] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  // Get the current session
  const { data: currentSession } = useFlowSessionById(flowSessionId || "", {
    enabled: !!flowSessionId,
  });

  // Get all sessions for this canvas; only main agent sessions appear in the
  // dropdown — sub-agent sessions (action_item_id set) are shown in the Explorer.
  const { data: existingSessions = [] } = useCanvasFlowSessions(canvasId, {
    enabled: !!canvasId,
  });
  const mainSessions = existingSessions.filter((s) => !s.action_item_id);

  // Define callbacks before early return
  const handleCreateNew = useCallback(async () => {
    if (!canvasId) return;

    setIsCreatingNew(true);
    try {
      const newSession = await createFlowSession.mutateAsync({
        mode: FLOW_SESSION_MODES.CANVAS_CHAT,
        class: "canvas",
        canvas_id: canvasId,
      });

      // Invalidate sessions list so dropdown appears when we now have multiple sessions
      queryClient.invalidateQueries({
        queryKey: ["canvas-flow-sessions", canvasId],
      });

      onOpenSession(newSession.id, "replace");

      setShowSessionSelector(false);
    } catch (error) {
      console.error("Failed to create new session:", error);
      Sentry.captureException(
        error instanceof Error
          ? error
          : new Error("Failed to create new session"),
        {
          tags: { component: "CanvasSessionPane", operation: "createSession" },
          extra: { canvasId },
        },
      );
    } finally {
      setIsCreatingNew(false);
    }
  }, [canvasId, createFlowSession, onOpenSession]);

  const handleSwitchSession = useCallback(
    (sessionId: string) => {
      if (!canvasId) return;

      onOpenSession(sessionId, "replace");
      setShowSessionSelector(false);
    },
    [canvasId, onOpenSession],
  );

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      if (!canvasId) return;

      try {
        await deleteFlowSession.mutateAsync(sessionId);

        // Invalidate the sessions list
        queryClient.invalidateQueries({
          queryKey: ["canvas-flow-sessions", canvasId],
        });

        // If we deleted the current session, navigate to another or close pane
        if (sessionId === flowSessionId) {
          const remainingSessions = mainSessions.filter(
            (s) => s.id !== sessionId,
          );

          if (remainingSessions.length > 0) {
            onOpenSession(remainingSessions[0].id, "replace");
          } else {
            onClose();
          }
        }

        // Keep dropdown open after delete (like CanvasTopBar does)
      } catch (error) {
        console.error("Failed to delete session:", error);
        Sentry.captureException(
          error instanceof Error
            ? error
            : new Error("Failed to delete session"),
          {
            tags: {
              component: "CanvasSessionPane",
              operation: "deleteSession",
            },
            extra: { canvasId, sessionId },
          },
        );
      }
    },
    [
      canvasId,
      flowSessionId,
      mainSessions,
      deleteFlowSession,
      queryClient,
      onOpenSession,
      onClose,
    ],
  );

  // Window header — name + session switcher on the left; the right cluster
  // holds New chat (with the create spinner), the windowed/full-height size
  // toggle, and minimize-to-FAB. The switcher dropdown is controlled so the
  // create/switch handlers can close it (and delete can keep it open).
  const header = (
    <FloatingChatHeader
      title={currentSession?.name || "Visual Agent"}
      updatedAt={currentSession?.updated_at}
      sessions={mainSessions.map((session) => ({
        key: session.id,
        id: session.id,
        name: session.name || "Untitled Session",
        updatedAt: session.updated_at,
      }))}
      currentSessionId={flowSessionId}
      onSwitchSession={handleSwitchSession}
      onDeleteSession={handleDeleteSession}
      deleteSessionAriaLabel="Delete session"
      onNewChat={handleCreateNew}
      newChatPending={isCreatingNew}
      newChatDisabled={isCreatingNew || !canvasId}
      canGoBack={canGoBack}
      onGoBack={onGoBack}
      isChatExpanded={isChatExpanded}
      onToggleExpand={onToggleExpand}
      onClose={onClose}
      menuOpen={showSessionSelector}
      onMenuOpenChange={(open) => {
        setShowSessionSelector(open);
        onMenuOpenChange?.(open);
      }}
    />
  );

  // Early return AFTER all hooks
  if (!canvasId) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <p className="text-sm text-muted-foreground">No canvas ID provided</p>
      </div>
    );
  }

  if (!flowSessionId) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <p className="text-sm text-muted-foreground">No session provided</p>
      </div>
    );
  }

  return (
    <FloatingChatPaneShell
      header={header}
      sessionKey={flowSessionId}
      initialSessionId={flowSessionId}
    >
      <CanvasSessionPaneInner
        flowSessionId={flowSessionId}
        canvasId={canvasId}
        referencedEntities={referencedEntities}
        onRemoveEntity={onRemoveEntity}
        clearTextSelectionsFromMessage={clearTextSelectionsFromMessage}
      />
    </FloatingChatPaneShell>
  );
}
